/**
 * Posting the structure confirmation — split out from `structure` (see that file) so the expensive OCR/task-
 * discovery pass can keep running in parallel with a concurrent submission split while the CARD it produces
 * still only appears once the split is resolved. Modeled on `gradingConsistency`'s async, queue-aware `ready()`
 * gate (`grading-consistency.job.ts`): the dependency between the two steps is expressed as "may I run now?",
 * not as a follow-up (which would fire only after `task` had already drained) or a stashed exam-state field.
 *
 * One row per exam, dispatched from `structure`'s own handler with the already-computed message content — this
 * type does no OCR/LLM work of its own, only the post.
 */

import { logger } from '@adornis/base/logging.js';
import { ConfirmationProgressStatus, setStructureConfirmationProgressByExam } from '../../../confirmation-progress.js';
import { requestStructureConfirmation } from '../../pipeline/structure-step.js';
import { structureExtractionEvents$ } from '../../start-structure-extraction.js';
import { waitForConversationFinishedThinking } from '../../submission-split.js';
import * as conditions from '../job-conditions.js';
import { anyLiveJobForTypes } from '../job-store.js';
import { taskTreeLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface StructureAnnouncePayload {
  conversationId: string;
  content: string;
  hasLowConfidence: boolean;
  [key: string]: unknown;
}

export const structureAnnounceJob = defineJob<StructureAnnouncePayload>({
  type: 'structureAnnounce',

  // Rewrites nothing in `exam.tasks`, but it is conceptually the closing half of the `task` step, so it shares
  // that step's lock rather than opting into unrelated parallelism.
  lockKey: examId => taskTreeLockKey(examId),

  /**
   * THE FIX. A `submissionSplit` dispatched alongside a blank exam's structure extraction can finish first — the
   * blank exam is one document, the split's LLM call runs over the whole multi-student PDF — so without this
   * gate the structure card could post before, or interleaved with, the split's. Both checks are needed: the
   * queue check covers the split still computing (before it has written anything to `examState` at all), the
   * exam-state check covers the split having posted its own card but the teacher not having confirmed it yet.
   */
  ready: async (exam, job) => {
    if (await anyLiveJobForTypes(job.examId, ['submissionSplit'])) {
      return { verdict: 'wait', because: 'structureAnnounce:split-in-flight' };
    }
    if (!conditions.splitConfirmedOrNotApplicable(exam)) {
      return { verdict: 'wait', because: 'structureAnnounce:split-unconfirmed' };
    }
    return 'run';
  },

  // Preserves the failure visibility `runStructureStep` used to provide inline before this post moved out of it.
  onTransition: async event => {
    if (event.to !== 'failed') return;
    await setStructureConfirmationProgressByExam(event.job.examId, {
      status: ConfirmationProgressStatus.FAILED,
      confirmed: false,
      message: 'progress.errorProcessingStructureConfirmation',
    });
  },

  handler: async (examId, payload) => {
    // Posting right after `ready()` opens can land while the agent is still mid-reply to whatever chat turn
    // confirmed the split (e.g. "please continue") — the card and canvas-open event would then appear BEFORE
    // the agent's own natural-language answer to that turn, which reads as backwards. The same wait the split
    // job already uses before posting its own card (`runSubmissionSplit`) fixes this the same way: bounded,
    // non-fatal, and a no-op if the turn has already finished by the time this row runs.
    await waitForConversationFinishedThinking(payload.conversationId);

    await requestStructureConfirmation(examId, payload.conversationId, payload.content, payload.hasLowConfidence);

    // The client opens the structure canvas on this event and refetches the exam — moved here from `structure`'s
    // own handler because the confirmation message this event refers to only exists once this row has run.
    structureExtractionEvents$.next({
      examID: examId,
      event: 'structure_confirmation_requested',
      message: payload.content,
      hasLowConfidence: payload.hasLowConfidence,
    });

    logger.info({ examId }, 'Structure confirmation posted');
    return { note: 'structure confirmation posted' };
  },
});
